import React, { useEffect, useState, useRef } from 'react';
import { Check, X, Camera, Trash2, ClipboardList, ChevronDown, ChevronUp, ImagePlus, MessageSquare, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import FormInput from '../../../../../Component/ui/FormInput.jsx';

const CHECK_ITEMS = [
  'Engine oil',
  'Diesel filter check',
  'Coolant',
  'Air filter',
  'Tie rod end',
  'Oil seal check',
  'Axle packing',
  'Shaft check',
  'Oil leakage',
  'Wheel nut bolts',
  'Wheel rim check',
  'Hose pipes',
  'Steering oil',
  'All pressure pipes',
  'Chassis greasing',
  'Headlight',
  'Tail light',
  'Indicator',
  'Battery lead',
  'Battery terminal',
  'Wiper blade',
  'All wiring check',
  'Meter check',
  'Front leaf spring',
  'Rear leaf spring',
  'Body frame / Chassis mounting check',
  'Diesel and urea tank cap',
  'Lift axle check',
  'Suspension greasing',
  'Welding work check',
  'Waterproof tarpaulin',
  'Plastic tarpaulin',
  'Floor tarpaulin',
  'Rope',
  'Belts',
  'Jack',
  'Jack rod',
  'Wheel spanner',
  'Stepney spanner',
  'Tyre lever'
];

const CheckList = ({ onPrevious, formData, setFormData, onSubmit, isSubmitting, onNext }) => {
  const [checklist, setChecklist] = useState([]);
  const [images, setImages] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});
  const [imageToView, setImageToView] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set(['item_0']));
  const fileInputRefs = useRef({});

  // Max image size (30MB)
  const MAX_SIZE_BYTES = 30 * 1024 * 1024;

  useEffect(() => {
    if (formData.checklist && formData.checklist.length > 0) {
      setChecklist(formData.checklist);
      // Load existing images if available
      if (formData.images) {
        setImages(formData.images);
        setImagePreviews(formData.imagePreviews || {});
      }
    } else {
      const initial = CHECK_ITEMS.map((label, idx) => ({
        key: `item_${idx}`,
        label,
        checked: null,
        remarks: ''
      }));
      setChecklist(initial);
      setFormData(prev => ({ ...prev, checklist: initial }));
    }
  }, []);

  const setStatus = (key, status) => {
    const updated = checklist.map(item => item.key === key ? { ...item, checked: status } : item);
    setChecklist(updated);
    setFormData(prev => ({ ...prev, checklist: updated }));
  };

  const handleRemarkChange = (key, value) => {
    const updated = checklist.map(item => item.key === key ? { ...item, remarks: value } : item);
    setChecklist(updated);
    setFormData(prev => ({ ...prev, checklist: updated }));
  };

  const handleFileChange = (event, key) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_SIZE_BYTES) {
      alert('Image size must be less than 30MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreviews(prev => ({
        ...prev,
        [key]: e.target.result
      }));
    };
    reader.readAsDataURL(file);

    // Store file
    setImages(prev => ({
      ...prev,
      [key]: file
    }));
  };

  const handleDeleteImage = (key) => {
    setImages(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setImagePreviews(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleReset = () => {
    const resetList = CHECK_ITEMS.map((label, idx) => ({
      key: `item_${idx}`,
      label,
      checked: null,
      remarks: ''
    }));
    setChecklist(resetList);
    setImages({});
    setImagePreviews({});
    setFormData(prev => ({ ...prev, checklist: resetList, images: {}, imagePreviews: {} }));
  };

  const handleSubmit = async () => {
    // Update formData with latest checklist and images
    setFormData(prev => ({ 
      ...prev, 
      checklist,
      images,
      imagePreviews
    }));
    if (onSubmit) {
      await onSubmit();
    }
  };

  const markedCount = checklist.filter(item => item.checked === true || item.checked === false).length;
  const okCount = checklist.filter(item => item.checked === true).length;
  const notOkCount = checklist.filter(item => item.checked === false).length;
  const allChecked = checklist.length > 0 && markedCount === checklist.length;

  const toggleExpand = (key) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allExpanded = expandedItems.size === checklist.length;

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set(checklist.map(item => item.key)));
    }
  };

  return (
    <div className="space-y-4 pb-24">
      {/* Heading */}
      <div className="pb-3 pl-2 mb-2 border-b border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <ClipboardList size={20} className="text-blue-600" />
          Vehicle Checklist
        </h3>
      </div>

      {/* Progress Summary */}
      <div className="flex items-center gap-3 px-1">
        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
            style={{ width: `${checklist.length > 0 ? (markedCount / checklist.length) * 100 : 0}%` }}
          />
        </div>
        <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
          {markedCount}/{checklist.length}
        </span>
      </div>

      {/* Status Chips */}
      <div className="flex gap-2 px-1 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
          <Check size={12} /> {okCount} OK
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
          <X size={12} /> {notOkCount} Not OK
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-50 text-gray-500 border border-gray-200">
          {checklist.length - markedCount} Pending
        </span>
      </div>

      {/* Expand All / Collapse All */}
      <div className="flex justify-end px-1">
        <button
          type="button"
          onClick={toggleExpandAll}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition"
        >
          {allExpanded ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      {/* Checklist Items */}
      <div className="max-h-[55vh] overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/50 divide-y divide-gray-100">
        {checklist.map((item, index) => {
          const isOk = item.checked === true;
          const isNotOk = item.checked === false;
          const isPending = item.checked === null;
          const isExpanded = expandedItems.has(item.key);
          const hasImage = !!imagePreviews[item.key];
          const hasRemarks = !!item.remarks;

          return (
            <div
              key={item.key}
              className={`transition-colors duration-200 ${
                isOk ? 'bg-green-50/60' : isNotOk ? 'bg-red-50/60' : 'bg-white'
              }`}
            >
              {/* Main Row */}
              <div className="flex items-center gap-2 px-3 py-3">
                {/* Number Badge */}
                <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  isOk ? 'bg-green-100 text-green-700' : isNotOk ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {index + 1}
                </span>

                {/* Label */}
                <span className="flex-1 text-sm font-medium text-gray-800 leading-snug">
                  {item.label}
                </span>

                {/* Indicator icons */}
                <div className="flex items-center gap-1">
                  {hasImage && <Camera size={13} className="text-blue-400" />}
                  {hasRemarks && <MessageSquare size={13} className="text-amber-400" />}
                </div>

                {/* OK / Not-OK buttons */}
                <div className="flex items-center gap-1 ml-1">
                  <button
                    type="button"
                    onClick={() => setStatus(item.key, true)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                      isOk
                        ? 'bg-green-500 text-white shadow-sm shadow-green-200'
                        : 'bg-gray-100 text-gray-400 hover:bg-green-50 hover:text-green-500'
                    }`}
                    aria-label="Mark OK"
                    title="OK"
                  >
                    <Check size={16} strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus(item.key, false)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                      isNotOk
                        ? 'bg-red-500 text-white shadow-sm shadow-red-200'
                        : 'bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500'
                    }`}
                    aria-label="Mark Not OK"
                    title="Not OK"
                  >
                    <X size={16} strokeWidth={2.5} />
                  </button>
                </div>

                {/* Expand toggle */}
                <button
                  type="button"
                  onClick={() => toggleExpand(item.key)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
                  title="Photo & Remarks"
                >
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {/* Expandable Area */}
              {isExpanded && (
                <div className="px-4 pb-3 pt-1 ml-9 mr-2 space-y-3 animate-[fadeIn_0.2s_ease-in]">
                  {/* Image Upload */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {imagePreviews[item.key] ? (
                      <div className="relative group">
                        <img
                          src={imagePreviews[item.key]}
                          alt={`Preview for ${item.label}`}
                          onClick={() => setImageToView(imagePreviews[item.key])}
                          className="h-20 w-20 object-cover rounded-xl border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-all shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(item.key)}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-transform hover:scale-110"
                          title="Delete image"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (!fileInputRefs.current[item.key]) {
                            fileInputRefs.current[item.key] = document.createElement('input');
                            fileInputRefs.current[item.key].type = 'file';
                            fileInputRefs.current[item.key].accept = 'image/*';
                            fileInputRefs.current[item.key].onchange = (e) => handleFileChange(e, item.key);
                          }
                          fileInputRefs.current[item.key].click();
                        }}
                        className="h-20 w-20 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/50 flex flex-col items-center justify-center gap-1 hover:bg-blue-100/60 hover:border-blue-400 transition-all cursor-pointer"
                        title="Upload image"
                      >
                        <ImagePlus size={20} className="text-blue-500" />
                        <span className="text-[10px] font-medium text-blue-500">Add Photo</span>
                      </button>
                    )}
                  </div>

                  {/* Remarks */}
                  <div>
                    <input
                      type="text"
                      placeholder="Add remarks (optional)"
                      value={item.remarks}
                      onChange={(e) => handleRemarkChange(item.key, e.target.value)}
                      className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 placeholder-gray-400 transition"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:relative md:border-t-0 md:mt-8 md:gap-4">
        <button
          onClick={onPrevious}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Previous
        </button>

        <button
          onClick={handleReset}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Reset
        </button>

        <button
          onClick={() => {
            if (!allChecked) return;
            // ensure parent formData has latest checklist before moving on
            setFormData(prev => ({ ...prev, checklist }));
            if (onNext) onNext();
          }}
          disabled={!allChecked}
          title={!allChecked ? 'Please mark all items (OK or Not OK) before proceeding' : 'Next'}
          className={`flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg ${!allChecked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'} transition font-semibold text-sm md:text-base shadow-md`}
        >
          Next
        </button>
      </div>

      {/* Image Preview Modal */}
      {imageToView && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-[9999] p-4"
          onClick={() => setImageToView(null)}
        >
          <div 
            className="relative bg-white rounded-lg max-w-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setImageToView(null)}
              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold"
              title="Close"
            >
              ✕
            </button>
            <img 
              src={imageToView} 
              alt="Full Preview"
              className="w-full h-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckList;
